import { useCallback, useEffect, useMemo, useState } from "react";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SettingsIcon from "@mui/icons-material/Settings";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { LocationCard } from "../components/LocationCard";
import { groupLocationsByRow } from "../utils/locations";

const ROWS = [...Array.from({ length: 20 }, (_, index) => index + 1), 99];
const QUICK_TRAY_WIDTH = 210;
const QUICK_TRAY_HEIGHT = 62;
const QUICK_TRAY_MARGIN = 8;
const HARDLINE_FILTERS = [
  { value: "all", label: "All locations" },
  { value: "hide", label: "Hide hardlines" },
  { value: "only", label: "Only hardlines" },
];

function clamp(value, min, max) {
  return Math.max(min, Math.min(value, max));
}

function QuickActionTray({
  location,
  position,
  waitForRelease,
  onDropped,
  onPartial,
  onRemove,
  onNote,
}) {
  if (!location) {
    return null;
  }

  return (
    <div
      className={`quick-action-tray ${waitForRelease ? "waiting-for-release" : ""}`}
      role="toolbar"
      aria-label="Quick location actions"
      style={{
        left: position.x,
        top: position.y,
      }}
    >
      <button type="button" className="quick-action dropped" aria-label="Toggle dropped" onClick={() => onDropped(location)}>
        <ArrowDownwardIcon />
      </button>
      <button type="button" className="quick-action partial" aria-label="Edit partial" onClick={() => onPartial(location)}>
        <Inventory2OutlinedIcon />
      </button>
      <button type="button" className="quick-action note" aria-label="Edit note" onClick={() => onNote(location)}>
        <NoteAltIcon />
      </button>
      <button type="button" className="quick-action remove" aria-label="Remove location" onClick={() => onRemove(location)}>
        <DeleteOutlineIcon />
      </button>
    </div>
  );
}

function ViewAllSkeleton() {
  return (
    <div className="view-all-skeleton" aria-hidden="true">
      {[1, 2].map((row) => (
        <section className="row-section" key={`view-skeleton-row-${row}`}>
          <div className="row-section-header skeleton-row-header">
            <span className="skeleton-line row-title" />
            <span className="skeleton-line icon-sized" />
          </div>
          <div className="location-grid view-all-location-grid">
            {[1, 2, 3, 4].map((card) => (
              <div
                className="location-card skeleton-card location-skeleton-card"
                key={`view-skeleton-card-${row}-${card}`}
              >
                <span className="skeleton-line code-sized" />
                <span className="skeleton-line subtitle-sized" />
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export function ViewAll({
  items,
  locations,
  loading,
  showEmptyRows,
  onModify,
  onSettings,
  onSelectLocation,
  onQuickDropped,
  onQuickPartial,
  onQuickRemove,
  onQuickNote,
}) {
  const [collapsedRows, setCollapsedRows] = useState({});
  const [quickActionLocation, setQuickActionLocation] = useState(null);
  const [hardlineFilter, setHardlineFilter] = useLocalStorage(
    "palletPicker.hardlineFilter",
    "all"
  );
  const filteredLocations = useMemo(() => {
    if (hardlineFilter === "hide") {
      return locations.filter((location) => !location.is_hardline);
    }
    if (hardlineFilter === "only") {
      return locations.filter((location) => location.is_hardline);
    }
    return locations;
  }, [hardlineFilter, locations]);
  const groups = useMemo(() => groupLocationsByRow(filteredLocations), [filteredLocations]);
  const itemsById = useMemo(
    () => new Map(items.map((item) => [item.id, item])),
    [items]
  );
  const itemIdsWithDroppedLocations = useMemo(
    () =>
      new Set(
        locations
          .filter((location) => location.is_dropped)
          .map((location) => location.item_id)
      ),
    [locations]
  );
  const hasItems = items.length > 0;
  const hasLocations = locations.length > 0;
  const hasFilteredLocations = filteredLocations.length > 0;
  const rowsToRender = ROWS.filter((row) => showEmptyRows || groups[row]?.length);

  const getQuickActionPosition = useCallback((anchor) => {
    const rect = anchor?.getBoundingClientRect();
    if (!rect) {
      return { x: QUICK_TRAY_MARGIN, y: QUICK_TRAY_MARGIN };
    }

    const maxX = window.innerWidth - QUICK_TRAY_WIDTH - QUICK_TRAY_MARGIN;
    const maxY = window.innerHeight - QUICK_TRAY_HEIGHT - QUICK_TRAY_MARGIN;
    return {
      x: clamp(
        rect.left + rect.width / 2 - QUICK_TRAY_WIDTH / 2,
        QUICK_TRAY_MARGIN,
        Math.max(QUICK_TRAY_MARGIN, maxX)
      ),
      y: clamp(
        rect.top + rect.height / 2 - QUICK_TRAY_HEIGHT / 2,
        QUICK_TRAY_MARGIN,
        Math.max(QUICK_TRAY_MARGIN, maxY)
      ),
    };
  }, []);

  const openQuickActions = useCallback(
    (locationRecord, anchor, options = {}) => {
      setQuickActionLocation({
        location: locationRecord,
        anchor,
        position: getQuickActionPosition(anchor),
        waitForRelease: Boolean(options.waitForRelease),
      });
    },
    [getQuickActionPosition]
  );

  useEffect(() => {
    if (!quickActionLocation) {
      return undefined;
    }

    const closeOnOutsidePointer = (event) => {
      if (event.target?.closest?.(".quick-action-tray")) {
        return;
      }
      setQuickActionLocation(null);
    };

    const updatePosition = () => {
      setQuickActionLocation((current) => {
        if (!current?.anchor?.isConnected) {
          return null;
        }
        return {
          ...current,
          position: getQuickActionPosition(current.anchor),
        };
      });
    };

    const enableTrayInteraction = () => {
      setQuickActionLocation((current) =>
        current ? { ...current, waitForRelease: false } : null
      );
    };

    window.addEventListener("pointerdown", closeOnOutsidePointer);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    if (quickActionLocation.waitForRelease) {
      window.addEventListener("pointerup", enableTrayInteraction);
      window.addEventListener("pointercancel", enableTrayInteraction);
    }
    return () => {
      window.removeEventListener("pointerdown", closeOnOutsidePointer);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("pointerup", enableTrayInteraction);
      window.removeEventListener("pointercancel", enableTrayInteraction);
    };
  }, [getQuickActionPosition, quickActionLocation]);

  const runQuickAction = (handler) => (location) => {
    setQuickActionLocation(null);
    handler(location);
  };

  return (
    <>
      <div className="top-actions view-all-actions">
        <button type="button" className="touch-button" onClick={onModify}>
          <EditIcon /> MODIFY
        </button>
        <button
          type="button"
          className="icon-button"
          aria-label="Open settings"
          onClick={onSettings}
        >
          <SettingsIcon />
        </button>
      </div>
      <section className="panel view-all-panel">
        <div className="page-title-row">
          <div className="round-icon">
            <LocationOnIcon />
          </div>
          <div>
            <h1>All Pallet Locations</h1>
            <div className="helper-text">Tap any location to view actions.</div>
          </div>
        </div>

        <div className="view-filter-bar" role="group" aria-label="Hardline filter">
          {HARDLINE_FILTERS.map((filter) => (
            <button
              key={filter.value}
              type="button"
              className={`segmented-button filter-button ${
                hardlineFilter === filter.value ? "active" : ""
              }`}
              onClick={() => setHardlineFilter(filter.value)}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {loading && (
          <>
            <div className="loading-banner" role="status">
              <span className="loading-spinner" />
              Loading pallet locations
            </div>
            <ViewAllSkeleton />
          </>
        )}

        {!loading && !hasLocations && (
          <div className="empty-state requirement-block">
            <InfoOutlinedIcon color="primary" />
            <h2>{hasItems ? "No saved locations yet" : "No items yet"}</h2>
            <p>
              {hasItems
                ? "Save at least one row and bay for an item before View All has pallet locations to display."
                : "Create an item in Modify, then save a row and bay before View All has pallet locations to display."}
            </p>
            <button type="button" className="touch-button" onClick={onModify}>
              <EditIcon /> MODIFY
            </button>
          </div>
        )}

        {!loading && hasLocations && !hasFilteredLocations && !showEmptyRows && (
          <div className="empty-state requirement-block">
            <InfoOutlinedIcon color="primary" />
            <h2>No locations match this filter</h2>
            <p>Try showing all locations or switching to a different hardline view.</p>
            <button
              type="button"
              className="touch-button"
              onClick={() => setHardlineFilter("all")}
            >
              Show all locations
            </button>
          </div>
        )}

        {!loading && hasLocations && rowsToRender.map((row) => (
          <section
            className={`row-section ${!groups[row]?.length ? "empty-row-section" : ""}`}
            key={row}
          >
            <button
              type="button"
              className="row-section-header"
              onClick={() =>
                setCollapsedRows((current) => ({
                  ...current,
                  [row]: !current[row],
                }))
              }
            >
              <span>
                <ViewModuleIcon /> ROW {row}
              </span>
              {collapsedRows[row] ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
            </button>
            {groups[row]?.length ? (
              !collapsedRows[row] && (
              <div className="location-grid view-all-location-grid">
                {groups[row].map((location) => (
                  <div className="quick-action-anchor" key={location.id}>
                    <LocationCard
                      location={location}
                      showItemName
                      compact
                      prodNeeded={itemsById.get(location.item_id)?.prod_needed}
                      hasDroppedSibling={
                        itemIdsWithDroppedLocations.has(location.item_id) &&
                        !location.is_dropped
                      }
                      onClick={(locationRecord) => {
                        setQuickActionLocation(null);
                        onSelectLocation(locationRecord);
                      }}
                      onQuickActions={openQuickActions}
                    />
                    {quickActionLocation?.location?.id === location.id && (
                      <QuickActionTray
                        location={location}
                        position={quickActionLocation.position}
                        waitForRelease={quickActionLocation.waitForRelease}
                        onDropped={runQuickAction(onQuickDropped)}
                        onPartial={runQuickAction(onQuickPartial)}
                        onRemove={runQuickAction(onQuickRemove)}
                        onNote={runQuickAction(onQuickNote)}
                      />
                    )}
                  </div>
                ))}
              </div>
              )
            ) : (
              null
            )}
          </section>
        ))}

        {!loading && hasLocations && (
          <div className="status-legend">
            <InfoOutlinedIcon color="success" />
            <strong>Status legend:</strong>
            <span className="legend-dropped">Dropped (blue),</span>
            <span className="legend-note">Note (yellow dot),</span>
            <span className="legend-partial">Partial (green badge).</span>
          </div>
        )}
      </section>
    </>
  );
}
